/**
 * @spec docs/specs/features/staking-position-ui.md#framework-portability-patterns
 * Environment variable wrapper for framework portability
 *
 * Single source of truth for all environment variables.
 * To migrate to Next.js: only change this file to use process.env.NEXT_PUBLIC_*
 *
 * NOTE: After the CHIP migration the staking UI is Base-only. Polygon-specific
 * env vars below (polygonUsdtAddress, permitForwarderAddress)
 * are retained ONLY because the legacy USDT-redemption hooks still
 * read them. They will be removed when those features are torn down by the
 * cross-chain decom workstream.
 */

/** True for a real build of a deployed environment (not local, not the demo build). */
function isDeployedBuild(): boolean {
  if (__DEMO__) return false;
  const environment = import.meta.env.VITE_ENVIRONMENT as string | undefined;
  return (
    environment === 'dev' ||
    environment === 'staging' ||
    environment === 'prod' ||
    /^feat-\d+$/.test(environment ?? '')
  );
}

export const env = {
  // Privy Configuration
  privyAppId: (() => {
    switch (import.meta.env.VITE_ENVIRONMENT) {
      case 'prod':
        return 'clvwrzvit049tejn7zkksautg';
      case 'staging':
        return 'clvl2h3t4009z4zhrp83gopvn';
      case 'dev':
        return 'clvwrwuyh01tzt3q7h7wdixq1';
      default:
        // on our local, to test privy tokens we default to production privy app id
        return 'clvwrzvit049tejn7zkksautg';
    }
  })(),

  // Data Sources
  stakingIndexerUrl: import.meta.env.VITE_STAKING_INDEXER_URL,
  rewardsApiUrl: import.meta.env.VITE_REWARDS_API_URL,
  claimApiUrl: import.meta.env.VITE_CLAIM_API_URL,
  swapApiUrl: import.meta.env.VITE_SWAP_API_URL,
  walletsApiUrl: import.meta.env.VITE_WALLETS_API_URL,
  /**
   * Base URL for the poker API (apps/server) — hosts the public
   * `/chip-issuer/claimant-registration` lazy-registration endpoint.
   */
  pokerApiUrl: import.meta.env.VITE_POKER_API_URL,

  /**
   * Base chain id the app signs and reads for: 8453 (mainnet, default) or 84532 (Base Sepolia,
   * local testnet runs only). Chain reads go through rewards-api's `/rpc/base` relay
   * (lib/provider.ts); no RPC URL is configured in the browser.
   */
  baseChainId: import.meta.env.VITE_BASE_CHAIN_ID === '84532' ? 84532 : 8453,

  // Block Explorers (Base only post CHIP migration)
  baseExplorerUrl: import.meta.env.VITE_BASE_EXPLORER_URL,

  // Contract Addresses (Base Mainnet)
  contracts: {
    phnmTokenAddress: import.meta.env.VITE_PHNM_TOKEN_ADDRESS,
    stakingAddress: import.meta.env.VITE_STAKING_ADDRESS,
    claimAddress: import.meta.env.VITE_CLAIM_ADDRESS,
    repositoryAddress: import.meta.env.VITE_REPOSITORY_ADDRESS,
    swapRelayerAddress: import.meta.env.VITE_SWAP_RELAYER_ADDRESS,
    usdcTokenAddress: import.meta.env.VITE_USDC_TOKEN_ADDRESS,
    aerodromePoolAddress: import.meta.env.VITE_AERODROME_POOL_ADDRESS,
    /** CHIP token (ERC-20, 6 decimals) on Base. */
    chipAddress: import.meta.env.VITE_CHIP_ADDRESS,
    /** ReserveAddressRegistry proxy on Base — backs the Reserves Transparency page (/reserves). */
    reserveRegistryAddress: import.meta.env.VITE_RESERVE_REGISTRY_ADDRESS,
    /** CHIPIssuer address on Base — redeem/redeemWithPermit target for CHIP→USDC withdrawals. */
    chipIssuerAddress: import.meta.env.VITE_CHIP_ISSUER_ADDRESS,
    /** RewardsDistributor address on Base — CHIP-rail claim() target (token dashboard dual-rail claims). */
    distributorAddress: import.meta.env.VITE_REWARDS_DISTRIBUTOR_ADDRESS,
    /** UserRegistry address on Base — isUser() read backing the CHIP-rail claim state machine. */
    userRegistryAddress: import.meta.env.VITE_USER_REGISTRY_ADDRESS,
    /**
     * PhenomDelegate7702V2 address on Base — the target of lazy v1→v2 re-delegation before a
     * USDC→PHNM buy. MUST equal poker's `config.contracts.phenomDelegate7702` per env (they flip to
     * v2 together in the rollout); drift is caught at runtime by re-checking detectReceiveAuthStrategy.
     */
    phenomDelegate7702: import.meta.env.VITE_PHENOM_DELEGATE_7702,

    // ----- Deprecated (cross-chain decom workstream will remove) -----
    /** @deprecated Polygon USDT redemption — slated for removal post CHIP cutover. */
    usdtRedemptionAddress: import.meta.env.VITE_USDT_REDEMPTION_ADDRESS,
    /** @deprecated Polygon USDT token — slated for removal post CHIP cutover. */
    polygonUsdtAddress: import.meta.env.VITE_POLYGON_USDT_ADDRESS,
    /** @deprecated Polygon permit forwarder — slated for removal post CHIP cutover. */
    permitForwarderAddress: import.meta.env.VITE_PERMIT_FORWARDER_ADDRESS,
  },

  /**
   * @deprecated Polygon explorer URL — retained as a no-op fallback (see above).
   */
  polygonExplorerUrl: import.meta.env.VITE_POLYGON_EXPLORER_URL,

  /**
   * Lending interest sign-ups are open. Off unless VITE_LENDING_INTEREST_ENABLED is exactly
   * "true": turn it on per environment only once that environment's wallets-api serves
   * POST /interest/lending. Off, /lending still explains the product and says sign-ups open soon.
   */
  lendingInterestEnabled: import.meta.env.VITE_LENDING_INTEREST_ENABLED === 'true',

  /**
   * Cloudflare Turnstile site key (public, not a secret) for the Lending interest form
   * (wallets-api POST /interest/lending). Unset locally and in demo mode: the widget is skipped.
   * A deployed build without it disables the form (see turnstileRequired); CI fails a staging or
   * prod build that enables sign-ups without it (.gitlab/token-client.yml).
   */
  turnstileSiteKey: (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || undefined,

  /**
   * Deployed environments (dev, staging, prod, feat-N) verify every interest sign-up with
   * Turnstile, so the form needs a site key there. Local runs and the demo build do not.
   */
  turnstileRequired: isDeployedBuild(),

  // Token Configuration (environment-specific)
  // Dev/testnet may use 18 decimals for USDT, production uses 6
  usdtDecimals: parseInt(import.meta.env.VITE_USDT_DECIMALS || '6', 10),
};

// Runtime validation - warn about missing required variables
const requiredVars = {
  VITE_PRIVY_APP_ID: env.privyAppId,
  VITE_STAKING_INDEXER_URL: env.stakingIndexerUrl,
  VITE_REWARDS_API_URL: env.rewardsApiUrl,
  VITE_CLAIM_API_URL: env.claimApiUrl,
};

Object.entries(requiredVars).forEach(([key, value]) => {
  if (!value) {
    console.warn(`Missing required environment variable: ${key}`);
  }
});

// Optional variables (nice to have)
const optionalVars = {
  VITE_SWAP_API_URL: env.swapApiUrl,
  VITE_WALLETS_API_URL: env.walletsApiUrl,
  VITE_CHIP_ADDRESS: env.contracts.chipAddress,
  VITE_RESERVE_REGISTRY_ADDRESS: env.contracts.reserveRegistryAddress,
  VITE_CHIP_ISSUER_ADDRESS: env.contracts.chipIssuerAddress,
  VITE_POKER_API_URL: env.pokerApiUrl,
  VITE_REWARDS_DISTRIBUTOR_ADDRESS: env.contracts.distributorAddress,
  VITE_USER_REGISTRY_ADDRESS: env.contracts.userRegistryAddress,
};

Object.entries(optionalVars).forEach(([key, value]) => {
  if (!value) {
    console.info(`Optional environment variable not set: ${key}`);
  }
});

// The Lending interest form refuses to submit without a Turnstile site key in a deployed build.
if (env.lendingInterestEnabled && env.turnstileRequired && !env.turnstileSiteKey) {
  console.error('VITE_TURNSTILE_SITE_KEY is not set — the Lending interest form is disabled');
}

// The CHIP claim panel always renders (the dual-rail flag was removed at the
// 2026-08-31 cutover), so a missing value silently breaks it — warn loudly
// instead of falling back to the quiet console.info above.
{
  const chipRailVars = {
    VITE_REWARDS_DISTRIBUTOR_ADDRESS: env.contracts.distributorAddress,
    VITE_USER_REGISTRY_ADDRESS: env.contracts.userRegistryAddress,
    VITE_POKER_API_URL: env.pokerApiUrl,
  };
  Object.entries(chipRailVars).forEach(([key, value]) => {
    if (!value) {
      console.warn(`${key} is not set — the CHIP claim panel will fail`);
    }
  });
}
